import React, { useCallback, useState, forwardRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Menu, AttachmentIcon, IconButton, Tooltip, Button } from "@activecollab/components";
import PropTypes from "prop-types";
import { fetchExpense } from "~/store/actions/actions.expenses";
import { AttachmentList } from "./AttachmentList";
import { AttachmentsPicker } from "../select_attachments/menu/AttachmentsPicker";

const Target = forwardRef(({ title, open, ...rest }, ref) => {
  return (
    <Tooltip title={title}>
      <IconButton
        {...rest}
        type="button"
        variant="text gray"
        size="small"
        active={open}
        ref={ref}
      >
        <AttachmentIcon />
      </IconButton>
    </Tooltip>
  );
});

Target.displayName = "Target";

Target.propTypes = {
  title: PropTypes.string,
  open: PropTypes.bool,
};

const AttachmentsPickerTarget = forwardRef(({ open, ...rest }, ref) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="tertiary"
      size="small"
      active={open}
      ref={ref}
      {...rest}
    >
      {t("+ Attach Files")}
    </Button>
  );
});

AttachmentsPickerTarget.propTypes = {
  open: PropTypes.bool,
};

AttachmentsPickerTarget.displayName = "AttachmentsPickerTarget";

export const AttachmentMenu = ({
  open: defaultOpen,
  parentType,
  parentId,
  attachmentsCount,
  projectId,
  canEdit,
  openFileWindow,
  onAttachFromDropbox,
  onAttachFromGoogleDrive,
  onRetry,
  onRemove,
  uploadsList,
  filesToAttach,
  onDeleteAttachment,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen);
  const dispatch = useDispatch();

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  useEffect(() => {
    open && dispatch(fetchExpense(projectId, parentId));
  }, [dispatch, open, parentId, projectId]);

  const handleDelete = useCallback(
    (id) => {
      typeof onDeleteAttachment === "function" && onDeleteAttachment({ drop_attached_files: [id] });
    },
    [onDeleteAttachment]
  );

  return (
    <Menu
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      position="bottom-start"
      target={
        <Target
          title={attachmentsCount === 1 ? t("1 attachment") : attachmentsCount + " " + t("attachments")}
          open={open}
        />
      }
      mode="wider"
    >
      <>
        <AttachmentList
          parentId={parentId}
          parentType={parentType}
          onDelete={canEdit ? handleDelete : null}
          onRetry={canEdit ? onRetry : null}
          uploadsList={uploadsList}
          filesToAttach={filesToAttach}
          onRemove={canEdit ? onRemove : null}
          canEdit={canEdit}
        />
        {canEdit ? (
          <div className="tw-p-2 tw-border-t tw-border-solid tw-border-0 tw-border-border-primary">
            <AttachmentsPicker
              target={<AttachmentsPickerTarget />}
              openFileWindow={openFileWindow}
              onAttachFromDropbox={onAttachFromDropbox}
              onAttachFromGoogleDrive={onAttachFromGoogleDrive}
            />
          </div>
        ) : null}
      </>
    </Menu>
  );
};

AttachmentMenu.propTypes = {
  parentId: PropTypes.number.isRequired,
  projectId: PropTypes.number.isRequired,
  open: PropTypes.bool,
  parentType: PropTypes.string,
  attachmentsCount: PropTypes.number,
  canEdit: PropTypes.bool,
  openFileWindow: PropTypes.func,
  onAttachFromDropbox: PropTypes.func,
  onAttachFromGoogleDrive: PropTypes.func,
  onRetry: PropTypes.func,
  onRemove: PropTypes.func,
  uploadsList: PropTypes.array,
  filesToAttach: PropTypes.array,
  onDeleteAttachment: PropTypes.func,
};

AttachmentMenu.defaultProps = {
  parentType: "Expense",
  filesToAttach: [],
};
